import * as React from 'react';
import { IActionButton, IButtonVisibilityState } from './ActionBarButtonTypes';

export const useButtonConfiguration = (allButtons: IActionButton[], containerWidth: number): IButtonVisibilityState => {
	// Calculate which buttons should be visible vs. in the more menu
	const calculateButtonVisibility = React.useCallback(() => {
		// If no width information yet, return all buttons as visible
		if (!containerWidth || containerWidth === 0) {
			const allButtonKeys = allButtons.map(btn => btn.key) as string[];
			return {
				visibleButtons: allButtonKeys,
				moreMenuButtons: [],
			};
		}

		// Filter buttons that should be shown based on their showWhen condition
		const buttonsToShow = allButtons.filter(btn => btn.showWhen === undefined || btn.showWhen());

		// If no buttons to show, return empty arrays
		if (buttonsToShow.length === 0) {
			return { visibleButtons: [], moreMenuButtons: [] };
		}

		// Check if we're in icon-only mode (matches the breakpoint in the stylesheet)
		const isIconOnlyMode = containerWidth <= 1080;

		// Calculate button widths based on mode
		const buttonWidth = isIconOnlyMode ? 50 : 135;
		const moreButtonWidth = 50;
		const containerPadding = 40;

		// Reserve space for the More button
		const availableWidth = containerWidth - containerPadding - moreButtonWidth;

		// Calculate how many buttons we can fit
		const maxVisibleButtons = Math.floor(availableWidth / buttonWidth);

		// Extract button keys for visible buttons
		const buttonKeys = buttonsToShow.map(btn => btn.key) as string[];

		// If we can fit all buttons, we don't need the More menu
		if (maxVisibleButtons >= buttonsToShow.length) {
			return {
				visibleButtons: buttonKeys,
				moreMenuButtons: [],
			};
		}

		// Otherwise, split buttons between toolbar and More menu
		return {
			visibleButtons: buttonKeys.slice(0, maxVisibleButtons),
			// Return the actual button objects for the more menu
			moreMenuButtons: buttonsToShow.slice(maxVisibleButtons),
		};
	}, [allButtons, containerWidth]);

	return calculateButtonVisibility();
};
